import { Fraction } from 'fractional'
import { getAttribute } from 'shopify-cartjs'

export function convertPixelsToRem(px) {
  // Gets font size of root element of the document
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)

  // console.log(px / rootFontSize);
  return px / rootFontSize
}

export function convertRemToPixels(rem) {
  // Gets font size of root element of the document
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)

  // console.log(rem * rootFontSize);
  return rem * rootFontSize
}

export function getViewport() {
  const w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
  const h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
  const dim = {
    width: w,
    height: h
  }
  return dim
}

/**
 * watchAwaitSelector
 * * Uses mutation observer to watch for 'selector' involved in DOM changes (added, removed, class change, content change, etc)
 * @param {Function} callback A callback function to be run whenever DOM changes are detected, can be anonymous or named
 * @param {String} selector Element(s) to look for involved in DOM changes, you can watch multiple selectors by separating via comma '.class1, .class2, #id3'
 * @param {Node} rootNode Node to watch from, typically document
 * @param {Number} fallbackDelay Interval to be used if the browser does not support MutationObserver, value is in milliseconds (2000 = 2 seconds)
 */
export const awaitSelector = (selector, rootNode, fallbackDelay) =>
  new Promise((resolve, reject) => {
    try {
      const root = rootNode || document
      const ObserverClass = MutationObserver || WebKitMutationObserver || null
      const mutationObserverSupported = typeof ObserverClass === 'function'
      let observer
      const stopWatching = () => {
        if (observer) {
          if (mutationObserverSupported) {
            observer.disconnect()
          } else {
            clearInterval(observer)
          }
          observer = null
        }
      }
      const findAndResolveElements = () => {
        const allElements = root.querySelectorAll(selector)
        if (allElements.length === 0) return
        const newElements = []
        const attributeForBypassing = 'data-awaitselector-resolved'
        allElements.forEach((el, i) => {
          if (typeof el[attributeForBypassing] === 'undefined') {
            allElements[i][attributeForBypassing] = ''
            newElements.push(allElements[i])
          }
        })
        if (newElements.length > 0) {
          stopWatching()
          resolve(newElements)
        }
      }
      if (mutationObserverSupported) {
        observer = new ObserverClass((mutationRecords) => {
          const nodesWereAdded = mutationRecords.reduce(
            (found, record) => found || (record.addedNodes && record.addedNodes.length > 0),
            false
          )
          if (nodesWereAdded) {
            findAndResolveElements()
          }
        })
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeOldValue: true,
          characterData: true,
          characterDataOldValue: true
        })
      } else {
        observer = setInterval(findAndResolveElements, fallbackDelay || 250)
      }
      findAndResolveElements()
    } catch (exception) {
      reject(exception)
    }
  })

export const watchAwaitSelector = (callback, selector, rootNode, fallbackDelay) => {
  ; (function awaiter(continueWatching = true) {
    if (continueWatching === false) return
    awaitSelector(selector, rootNode, fallbackDelay).then(callback).then(awaiter)
  })()
}

export function throttle(type, name, obj) {
  let running = false

  obj = obj || window

  const func = function () {
    if (running) return

    running = true

    requestAnimationFrame(function () {
      obj.dispatchEvent(new CustomEvent(name))
      running = false
    })
  }

  obj.addEventListener(type, func)
}

export function debounce(func, wait, immediate) {
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export function insertAfter(el, referenceNode) {
  referenceNode.parentNode.insertBefore(el, referenceNode.nextSibling)
}

export function removeClass(elements, className) {
  if (elements.length > 0) {
    for (const element of elements) {
      element.classList.remove(className)
    }
  }
}

export function resizeCompareImages(gridItemsEl, imageWrappers) {
  const images = gridItemsEl.querySelectorAll('img')

  for (const image of images) {
    image.style.height = 'auto'
  }

  for (const wrapper of imageWrappers) {
    wrapper.style.height = 'auto'
  }

  if (images.length > 0) {
    const firstImage = images[0]

    const { height } = firstImage.getBoundingClientRect()
    let imageHeight = `${height}px`

    if (height <= 0) {
      imageHeight = 'auto'
    }

    for (const wrapper of imageWrappers) {
      wrapper.style.height = imageHeight
    }

    for (const image of images) {
      image.style.height = imageHeight
    }
  }
}

export function getNumPerRow(gridItems) {
  if (gridItems.length > 0) {
    const baseOffset = gridItems[0].offsetTop
    const breakIndex = gridItems.findIndex((item) => item.offsetTop > baseOffset)
    const numPerRow = breakIndex === -1 ? gridItems.length : breakIndex
    return numPerRow
  }
}

export function outerWidth(el) {
  let width = el.offsetWidth
  const style = getComputedStyle(el)

  width += parseInt(style.marginLeft) + parseInt(style.marginRight)
  return width
}

export function equalHeights(selector, matchTallest = true) {
  const elements = document.querySelectorAll(selector)

  for (let i = 0; i < elements.length; i++) {
    elements[i].style.height = ''
  }

  if (matchTallest === true) {
    let tallest = 0
    for (let i = 0; i < elements.length; i++) {
      const ele = elements[i]
      const eleHeight = ele.offsetHeight
      tallest = eleHeight > tallest ? eleHeight : tallest
    }

    for (let i = 0; i < elements.length; i++) {
      elements[i].style.height = `${tallest}px`
    }
  } else {
    let shortest = elements[0].offsetHeight
    for (let i = 0; i < elements.length; i++) {
      const ele = elements[i]
      const eleHeight = ele.offsetHeight
      shortest = eleHeight < shortest ? eleHeight : shortest
    }

    for (let i = 0; i < elements.length; i++) {
      elements[i].style.height = `${shortest}px`
    }
  }
}

export function numWords(num) {
  const a = [
    '',
    'one ',
    'two ',
    'three ',
    'four ',
    'five ',
    'six ',
    'seven ',
    'eight ',
    'nine ',
    'ten ',
    'eleven ',
    'twelve ',
    'thirteen ',
    'fourteen ',
    'fifteen ',
    'sixteen ',
    'seventeen ',
    'eighteen ',
    'nineteen '
  ]
  const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

  const regex = /^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/

  if ((num = num.toString()).length > 9) {
    throw new Error('overflow') // Does not support converting more than 9 digits yet
  }

  const n = `000000000${num}`.substr(-9).match(regex)
  if (!n) return

  let str = ''
  str += n[3] != 0 ? `${a[Number(n[3])] || `${b[n[3][0]]} ${a[n[3][1]]}`}thousand ` : ''
  str += n[4] != 0 ? `${a[Number(n[4])] || `${b[n[4][0]]} ${a[n[4][1]]}`}hundred ` : ''
  str += n[5] != 0 ? (str != '' ? 'and ' : '') + (a[Number(n[5])] || `${b[n[5][0]]} ${a[n[5][1]]}`) : ''

  return str.trim()
}

export function setBundlePriceNew(priceButtonBase, parts, patternBundleSubmit, removeDisable = false) {
  if (!patternBundleSubmit.hasAttribute('disabled') || removeDisable === true) {
    let totalCost = 0
    let canAddBundle = true

    for (let i = 0; i < parts.length; i++) {
      let selected = parts[i].popover.querySelector('.is-selected')
      if (selected && selected.closest('button')) {
        selected = selected.closest('button')
      }

      if (selected) {
        const quantity = Number(parts[i].quantityEl.value)
        const price = Number(selected.dataset.price)
        const inventory = Number(selected.dataset.inventory)
        const message = parts[i].quantityEl.closest('.ProductForm__Variants').querySelector('#variant-message')

        totalCost += (price * quantity) / 100

        // const inputs = document.querySelectorAll('.inputs');
        // for (i = 0; i < inputs.length; i++) {
        //   console.log(`the user has selected ${inputs[i].value} `);
        // }

        // console.log(`price is ${price / 100}, quantity is ${quantity}: total cost is ${totalCost}`)
        console.log(`The bundle needs ${quantity} and there are ${inventory} in stock!!!!!!!! Price is ${price}`)
        console.log(selected)
        console.log(selected.dataset.inventory)


        if (inventory >= quantity) {
          // console.log('there is enough stock of the variant to complete this order')
          if (message) {
            message.innerHTML = ''
            message.classList.remove('out-of-stock')
          }
        } else {
          // console.log('there is not enough variant')
          canAddBundle = false
          if (message) {
            message.innerHTML = 'Insufficient inventory. Change selection.'
            message.classList.add('out-of-stock')
          }
        }
      }
    }

    const button = document.getElementById('js-bundle-submit-button')
    const disabledButton = document.getElementById('disabled-button')

    if (canAddBundle) {
      button.style.display = 'flex'
      disabledButton.style.display = 'none'
    } else {
      button.style.display = 'none'
      disabledButton.style.display = 'flex'
    }

    // Price of the PDF itself
    const pdf_price = Number(patternBundleSubmit.dataset.price)

    totalCost += pdf_price

    patternBundleSubmit.setAttribute('data-total-cost', totalCost)
    patternBundleSubmit.removeAttribute('disabled')
    patternBundleSubmit.innerHTML = `${priceButtonBase}${totalCost.toFixed(2)}`
  }
}

export function setBundlePrice(priceButtonBase, variantInputs, quantityInputs, patternBundleSubmit) {
  let totalCost = 0

  for (let i = 0; i < variantInputs.length; i++) {
    const select = variantInputs[i]
    const quantityEl = quantityInputs[i]
    const selected = select.options[select.selectedIndex]
    const id = selected.dataset.variant

    const price = Number(selected.dataset.price)
    const quantity = Number(quantityEl.value)

    totalCost += price * quantity
    // console.log(`price is ${price}, quantity is ${quantity}: total cost is ${totalCost}`);

  }

  // Price of the PDF itself
  const pdf_price = Number(patternBundleSubmit.dataset.price)

  totalCost += pdf_price

  patternBundleSubmit.setAttribute('data-total-cost', totalCost)
  patternBundleSubmit.innerHTML = `${priceButtonBase}${totalCost.toFixed(2)}`
}

export function setBundleDescription(base, variantInputs, quantityInputs, patternBundleContents) {
  let bundleDescriptionText = base

  for (let i = 0; i < variantInputs.length; i++) {
    const select = variantInputs[i]
    const selected = select.options[select.selectedIndex]

    const num = quantityInputs[i].value
    let skeins = 'skeins'
    if (num == 1) {
      skeins = 'skein'
    }

    const numText = numWords(num)
    const name = toUpper(selected.dataset.yarnName)
    const variant = toUpper(selected.dataset.variantName)

    if (variantInputs.length === 1) {
      bundleDescriptionText += `<span class="weight-500">${numText}</span> ${skeins} of <span class="weight-500">${name} - ${variant}</span>. Need an extra skein or two? Adjust quantities manually using +/- for each color quantity.`
    } else if (i === variantInputs.length - 1) {
      bundleDescriptionText += `and <span class="weight-500">${numText}</span> ${skeins} of <span class="weight-500">${name} - ${variant}</span>. Need an extra skein or two? Adjust quantities manually using +/- for each color quantity.`
    } else {
      bundleDescriptionText += `<span class="weight-500">${numText}</span> ${skeins} of <span class="weight-500">${name} - ${variant}</span>, `
    }
  }

  // console.log(patternBundleContents);
  patternBundleContents.innerHTML = ''
  patternBundleContents.insertAdjacentHTML('afterbegin', bundleDescriptionText)
}

export function setBundleDescriptionNew(base, parts, patternBundleContents) {
  let bundleDescriptionText = base

  let validLength = 0

  for (let i = 0; i < parts.length; i++) {
    const quantity = Number(parts[i].quantityEl.value)
    let skeins = 'skeins'

    if (quantity === 1) {
      skeins = 'skein'
    }

    let selected = parts[i].popover.querySelector('.is-selected')

    if (selected && selected.closest('button')) {
      selected = selected.closest('button')
    }

    let numText = numWords(quantity)
    if (quantity === 0) {
      numText = 'zero'
    }

    if (selected && selected.dataset.productType && !selected.dataset.empty) {
      validLength++
    }

    console.log('selected is ', selected, 'numtext here is', numText)

    if (selected && selected.dataset.productType && selected.dataset.empty !== 'true') {
      if (selected.dataset.productType === 'yarn') {
        const name = toUpper(selected.dataset.yarnName)
        const variant = toUpper(selected.dataset.variantName)

        // if (validLength === 1) {
        //   bundleDescriptionText += `<span class="weight-500">${numText}</span> ${skeins} of <span class="weight-500">${name} - ${variant}</span>. Need an extra skein or two? Adjust quantities manually using +/- for each color quantity.`
        // } else if (validLength - 1 === i) {
        //   bundleDescriptionText += `and <span class="weight-500">${numText}</span> ${skeins} of <span class="weight-500">${name} - ${variant}</span>. Need an extra skein or two? Adjust quantities manually using +/- for each color quantity.`
        // } else {
        bundleDescriptionText = `Your bundle contains the selected yarn plus a digital copy of the pattern.`
        // }
      } else if (selected.dataset.productType === 'needle') {
        const name = toUpper(selected.dataset.title)
        bundleDescriptionText = `Your bundle contains the selected yarn and accessory plus a digital copy of the pattern.`
      }
    }

  }

  // console.log(patternBundleContents);
  // console.log(bundleDescriptionText);
  patternBundleContents.innerHTML = ''
  patternBundleContents.insertAdjacentHTML('afterbegin', bundleDescriptionText)
}

export function deactivateGalleryItems(navItems, imageWrappers, activeClass) {
  for (const item of navItems) {
    item.classList.remove(activeClass)
  }

  for (const image of imageWrappers) {
    image.classList.remove(activeClass)
  }
}

export function getMixedNumberHTML(number) {
  const integer = Math.floor(number)
  const decimal = number - integer

  if (decimal != 0) {
    const fraction = new Fraction(decimal)

    const html = `${integer} <span class="fraction-wrapper"><sup>${fraction.numerator}</sup>/<sub>${fraction.denominator}</sub></span> <span class="inches-wrapper">&rdquo;</span>`
    return html
  }

  return `${number} <span class="inches-wrapper">&rdquo;</span>`
}

export function updateRequiredQuantityValues(sizeInput, quantityInputs) {
  const req1 = sizeInput.dataset.yarn1
  const req2 = sizeInput.dataset.yarn2
  const req3 = sizeInput.dataset.yarn3
  const req4 = sizeInput.dataset.yarn4
  const req5 = sizeInput.dataset.yarn5
  const req6 = sizeInput.dataset.yarn6

  if (quantityInputs[0]) {
    quantityInputs[0].setAttribute('value', req1)
    quantityInputs[0].value = req1
  }

  if (quantityInputs[1]) {
    quantityInputs[1].setAttribute('value', req2)
    quantityInputs[1].value = req2
  }

  if (quantityInputs[2]) {
    quantityInputs[2].setAttribute('value', req3)
    quantityInputs[2].value = req3
  }

  if (quantityInputs[3]) {
    quantityInputs[3].setAttribute('value', req4)
    quantityInputs[3].value = req4
  }

  if (quantityInputs[4]) {
    quantityInputs[4].setAttribute('value', req5)
    quantityInputs[4].value = req5
  }

  if (quantityInputs[5]) {
    quantityInputs[5].setAttribute('value', req6)
    quantityInputs[5].value = req6
  }
}

export function equalWidths(selector, addExtra = 0) {
  let elements
  let widest = 0

  if (typeof selector !== 'string') {
    elements = selector
  } else {
    elements = document.querySelectorAll(selector)
  }

  for (let i = 0; i < elements.length; i++) {
    elements[i].style.height = ''
  }

  for (let i = 0; i < elements.length; i++) {
    const ele = elements[i]
    const eleWidth = ele.offsetWidth
    widest = eleWidth > widest ? eleWidth : widest
  }

  for (let i = 0; i < elements.length; i++) {
    elements[i].style.width = `${widest + addExtra}px`
  }
}

export function setHeightAuto(selector) {
  const elements = document.querySelectorAll(selector)
  for (const e of elements) {
    e.style.height = 'auto'
  }
}

export function findMax(array) {
  let max = 0
  const a = array.length
  let counter

  for (counter = 0; counter < a; counter++) {
    if (array[counter] > max) {
      max = array[counter]
    }
  }

  return max
}

export function updateSelectedOption(id, select) {
  const option = select.querySelector(`option[data-variant="${id}"]`)

  if (option) {
    select.value = option.dataset.value
  }
}

export function openCartDrawer() {
  const cartDrawer = document.getElementById('sidebar-cart')
  if (cartDrawer) {
    cartDrawer.setAttribute('aria-hidden', 'false')
  }
}

export function closeCartDrawer() {
  const cartDrawer = document.getElementById('sidebar-cart')
  if (cartDrawer) {
    cartDrawer.setAttribute('aria-hidden', 'true')
  }
}

export function setupCloseDrawer() {
  const cartDrawer = document.getElementById('sidebar-cart')
  const cartDrawerClose = cartDrawer.querySelector('.Drawer__Close')

  if (cartDrawerClose) {
    cartDrawerClose.addEventListener('click', closeCartDrawer)
  }
}

export function addToCart(variantID, quantity = 1) {
  const addData = {
    id: variantID,
    quantity
  }

  fetch(`${window.theme.localeRootUrl}/cart/add.js`, {
    body: JSON.stringify(addData),
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
    .then(function (response) {
      return response.json()
    })
    .then(function (json) {
      console.log(json)
    })
    .catch(function (err) {
      /* uh oh, we have error. */
      console.error(err)
    })
}

// classes param needs to be an array
export function replaceElement(element, newElementType, classes, url) {
  const newElement = document.createElement(newElementType)

  // If link element, add URL if it exists
  if (newElementType === 'a' && url !== undefined) {
    newElement.href = url
  }

  // Add any classes from original to new element
  if (element !== null && element !== undefined) {
    const classNames = element.className.trim().split(' ')

    if (classNames != '' && classNames.length > 0) {
      for (const name of classNames) {
        newElement.classList.add(name)
      }
    }
  }

  // Add any additional classes
  if (classes !== undefined && typeof classes === 'object') {
    // In case you need to customize/fix output with custom class
    newElement.classList.add(...classes)
  }

  // Wrap the element with the new element
  if (element !== null && newElement !== null) {
    element.parentNode.insertBefore(newElement, element)
    newElement.appendChild(element)
  }

  // Replace the element with its content
  element.outerHTML = element.innerHTML
}

export function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export function isVisible(domElement, minRatio = 1, callbackOnVisible, callbackOnElse) {
  return new Promise((resolve) => {
    const o = new IntersectionObserver(([entry]) => {
      resolve(entry.intersectionRatio >= minRatio)

      if (entry.intersectionRatio >= minRatio) {
        if (typeof callbackOnVisible === 'function') {
          callbackOnVisible()
        }
      } else if (typeof callbackOnElse === 'function') {
        callbackOnElse()
      }

      o.disconnect()
    })
    o.observe(domElement)
  })
}

export function checkIfVariantIsAvailable(variantID, variantsArray) {
  // console.log("variant array is...");
  // console.log(variantsArray);
  const matchingVariant = variantsArray.variants.filter((el) => {
    return Number(el.id) === Number(variantID)
  })

  if (matchingVariant && matchingVariant.length > 0) {
    return matchingVariant[0].available
  }
}

export function disableBundleSubmitButton(buttonElement) {
  buttonElement.setAttribute('disabled', 'disabled')
  buttonElement.textContent = "Can't Add Bundle - Out of Stock"
}

export function swapElements(obj1, obj2) {
  // save the location of obj2
  const parent2 = obj2.parentNode
  const next2 = obj2.nextSibling
  // special case for obj1 is the next sibling of obj2
  if (next2 === obj1) {
    // just put obj1 before obj2
    parent2.insertBefore(obj1, obj2)
  } else {
    // insert obj2 right before obj1
    obj1.parentNode.insertBefore(obj2, obj1)

    // now insert obj1 where obj2 was
    if (next2) {
      // if there was an element after obj2, then insert obj1 right before that
      parent2.insertBefore(obj1, next2)
    } else {
      // otherwise, just append as last child
      parent2.appendChild(obj1)
    }
  }
}

export function toUpper(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function available(submitButton, noStockButton) {
  if (submitButton) {
    submitButton.classList.remove('Button--secondary')
    submitButton.classList.add('Button--primary')
    submitButton.removeAttribute('disabled')
    submitButton.innerHTML = 'Add To Cart'
  }

  if (noStockButton) {
    noStockButton.classList.add('display-none')
  }
}

export function notAvailable(submitButton, noStockButton) {
  if (submitButton) {
    submitButton.setAttribute('disabled', 'disabled')
    submitButton.classList.remove('Button--primary')
    submitButton.classList.add('Button--secondary')
    submitButton.innerHTML = 'Sold Out'
  }

  if (noStockButton) {
    noStockButton.classList.remove('display-none')
  }
}

// ======================================= //
// === FUNCTIONS USED FOR COMPARE gridItems === //
// ======================================= //
export function activateItem(item) {
  item.active = true
  item.itemEl.classList.add('active')
}

export function deactivateItem(item) {
  item.active = false
  item.itemEl.classList.remove('active')
}

export function clearItem(item, swatch) {
  item.imageWrapper.innerHTML = ``
  item.textWrapper.innerHTML = ``
  item.active = false
  item.empty = true
  item.variantID = null
  item.itemEl.classList.add('empty')
}

export function deactivateItems(gridItems) {
  for (const [id, item] of Object.entries(gridItems)) {
    item.active = false
    item.itemEl.classList.remove('active')
  }
}

export function findItemByVariant(variantID, gridItems) {
  for (const [id, item] of Object.entries(gridItems)) {
    if (item.variantID === variantID) {
      return gridItems[id]
    }
  }
}

export function findActive(gridItems) {
  for (const [id, item] of Object.entries(gridItems)) {
    if (item.active === true) {
      return gridItems[id]
    }
  }

  // No active items were found
  return false
}

export function findEmpty(gridItems) {
  for (const [id, item] of Object.entries(gridItems)) {
    if (item.empty === true) {
      return gridItems[id]
    }
  }

  // No empty items were found
  return false
}

export function fillSlot(item, variantID) {
  // console.log("called fill slot");
  const data = window.yarnVariants.find((el) => Number(el.id) === Number(variantID))
  //   let data = window.yarnVariants[variantID];
  const url = data.imageURL

  if (item) {
    item.itemEl.classList.remove('empty')
    item.variantID = variantID
    // item.imageWrapper.style.outline = `1px solid ${data.color}`;
    item.imageWrapper.innerHTML = `<img src="${url}" draggable='false'>`
    item.textWrapper.innerHTML = `<p draggable='false'><strong draggable='false'>${toTitleCase(
      data.name
    )} - </strong> ${data.description}</p>`

    item.empty = false
    deactivateItem(item)
  }
}

export function activateSwatch(swatch) {
  swatch.classList.add('active')
}

export function deactivateSwatch(swatch, useVariantID = false) {
  if (useVariantID === true) {
    swatch = document.querySelector(`#compare-colors .swatch-circle[data-variant="${swatch}"]`)
  }

  swatch.classList.remove('active')
}

// Standalone version, so custom cart object (for yarn pages) isn't required
export function addToCartStandalone(variantID, quantity = 1) {
  const addData = {
    id: variantID,
    quantity
  }

  const id = Number(variantID)
  const products = {}
  products[id] = Number(quantity)
  document.dispatchEvent(new CustomEvent('theme:loading:start'))

  function updateCartAttributes(data, callback) {
    const params = {
      type: 'POST',
      url: '/cart/update.js',
      data,
      dataType: 'json',
      success(cart_data) {
        if (typeof callback === 'function') {
          document.dispatchEvent(new CustomEvent('theme:loading:end'))
          $('.Header__CartCount').html(cart_data.item_count)
          callback(cart_data)
          console.log(cart_data)
          const itemContainer = document.querySelector('#sidebar-cart .Drawer__Container')

          let itemList = document.querySelector('form.Cart .Cart__ItemList')
          const drawerButton = document.querySelector('.Drawer__Footer [data-money-convertible]')
          console.log(drawerButton)
          drawerButton.textContent = `$ ${cart_data.total_price / 100}`
          // now have access to Shopify cart object
          if (!itemList) {
            itemContainer.innerHTML = `
          <input type="hidden" name="attributes[collection_mobile_items_per_row]" value="">
          <input type="hidden" name="attributes[collection_desktop_items_per_row]" value="">
          <div class="Cart__ItemList"></div>`

            itemList = document.querySelector('#sidebar-cart .Cart__ItemList')
          }

          itemList.innerHTML = ''

          for (const item of cart_data.items) {
            const html = `<div class="CartItemWrapper"><div class="CartItem">
          <div class="CartItem__ImageWrapper AspectRatio">
            <div class="AspectRatio" style="--aspect-ratio: ${item.featured_image.aspect_ratio}">
              <img class="CartItem__Image" src="${item.featured_image.url}" alt="${item.featured_image.alt}">
            </div>
          </div>

          <div class="CartItem__Info">
            <h2 class="CartItem__Title Heading">
              <a href="${item.url}">${item.product_title}</a>
            </h2>

            <div class="CartItem__Meta Heading Text--subdued"><p class="CartItem__Variant">${item.variant_title
              }</p><div class="CartItem__PriceList"><span class="CartItem__Price Price" data-money-convertible="">$ ${item.final_line_price / 100
              }</span></div>
            </div><div class="CartItem__Actions Heading Text--subdued" style="text-align: center">
                <div class="CartItem__QuantitySelector">

                  <div class="QuantitySelector">
                  <a class="QuantitySelector__Button Link Link--primary" title="Set quantity to ${item.quantity - 1
              }" href="/cart/change?quantity=${item.quantity - 1}&amp;id=${item.key}" data-quantity="${item.quantity - 1
              }" data-line-id="${item.key
              }" data-action="update-item-quantity"><svg class="Icon Icon--minus" role="presentation" viewBox="0 0 16 2">
      <path d="M1,1 L15,1" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"></path>
    </svg></a>

                    <input type="text" name="updates[]" id="updates_${item.key
              }" class="QuantitySelector__CurrentQuantity" pattern="[0-9]*" data-line-id="${item.key}" value="${item.quantity
              }">

                    <a class="QuantitySelector__Button Link Link--primary" title="Set quantity to ${item.quantity + 1
              }" href="/cart/change?quantity=${item.quantity + 1}&amp;id=${item.key}" data-quantity="${item.quantity + 1
              }" data-line-id="${item.key
              }" data-action="update-item-quantity"><svg class="Icon Icon--plus" role="presentation" viewBox="0 0 16 16"><g stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"><path d="M8,1 L8,15"></path><path d="M1,8 L15,8"></path></g></svg></a>
                  </div>
                </div>

                <a href="/cart/change?quantity=0&amp;id=${item.key
              }" class="CartItem__Remove Link Link--underline Link--underlineShort" data-quantity="0" data-line-id="${item.key
              }" data-action="remove-item">Remove</a>
              </div>
          </div></div></div>`

            itemList.insertAdjacentHTML('beforeend', html)
          }
        } else {
          Shopify.api.onCartUpdate(cart_data)
        }
      },
      error(XMLHttpRequest, textStatus) {
        Shopify.api.onError(XMLHttpRequest, textStatus)
      }
    }
    $.ajax(params)
  }

  updateCartAttributes(
    {
      updates: products
    },
    function (cart_data) {
      console.log(cart_data)
    }
  )
  $(window).load(function () {
    if (window.location.href.indexOf('reload') == -1) {
      window.location.replace(`${window.location.href}?reload`)
    }
  })
}

// The original theme has nearly all of its cart/variant functionality tied in with the slider.
// Since the Yarn product page doesn't use a slider, it has several missing features.
// Some of the missing features are added here.
const cart = {
  elements: {
    itemContainer: document.querySelector('#sidebar-cart form.Cart .Drawer__Container'),
    itemList: document.querySelector('#sidebar-cart form.Cart .Cart__ItemList'),
    overlay: document.querySelector('.PageOverlay'),
    drawer: document.getElementById('sidebar-cart'),
    closeButton: document.querySelector('#sidebar-cart .Drawer__Close')
  },
  updateElements() {
    cart.elements.itemContainer = document.querySelector('#sidebar-cart form.Cart .Drawer__Container')
    cart.elements.itemList = document.querySelector('#sidebar-cart form.Cart .Cart__ItemList')
    cart.elements.overlay = document.querySelector('.PageOverlay')
    cart.elements.drawer = document.getElementById('sidebar-cart')
    cart.elements.closeButton = document.querySelector('#sidebar-cart .Drawer__Close')
  },
  openCartDrawer() {
    if (cart.elements.drawer) {
      cart.elements.drawer.setAttribute('aria-hidden', false)
    }
    if (cart.elements.overlay) {
      cart.elements.overlay.classList.add('is-visible')
    }
  },
  closeCartDrawer() {
    // console.log('Closing cart drawer...');
    if (cart.elements.drawer) {
      cart.elements.drawer.setAttribute('aria-hidden', true)
    }
    if (cart.elements.overlay) {
      cart.elements.overlay.classList.remove('is-visible')
    }
  },
  setupCloseButton() {
    if (cart.elements.closeButton) {
      cart.elements.closeButton.addEventListener('click', cart.closeCartDrawer)
    }
  },
  addToCart(variantID, quantity = 1) {
    const addData = {
      id: variantID,
      quantity
    }

    fetch(`${window.theme.localeRootUrl}/cart/add.js`, {
      body: JSON.stringify(addData),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (response) {
        return response.json()
      })
      .then(function (json) {
        console.log(json)
        cart.openCartDrawer()
      })
      .catch(function (err) {
        /* uh oh, we have error. */
        console.error(err)
      })
  },
  rebuildCart(cart_data, callback) {
    cart.closeCartDrawer()
    // console.log("new cart data is...");
    console.log(cart_data)

    const drawerButton = document.querySelector('.Drawer__Footer [data-money-convertible]')
    if (drawerButton) {
      console.log(drawerButton)
      drawerButton.textContent = `$ ${cart_data.total_price / 100}`
    }

    const itemContainer = document.querySelector('#sidebar-cart form.Cart .Drawer__Container')

    itemContainer.innerHTML = `
      <input type='hidden' name='attributes[collection_mobile_items_per_row]' value=''>
      <input type='hidden' name='attributes[collection_desktop_items_per_row]' value=''>
      <div class='Cart__ItemList'></div>`
    cart.updateElements()

    for (const item of cart_data.items) {
      console.log(item)
      let variantTitle
      if (item.variant_title === null) {
        variantTitle = ''
      } else {
        variantTitle = item.variant_title
      }

      const html = `<div class="CartItemWrapper"><div class="CartItem">
      <div class="CartItem__ImageWrapper AspectRatio">
        <div class="AspectRatio" style="--aspect-ratio: ${item.featured_image.aspect_ratio}">
          <img class="CartItem__Image" src="${item.featured_image.url}" alt="${item.featured_image.alt}">
        </div>
      </div>

      <div class="CartItem__Info">
        <h2 class="CartItem__Title Heading">
          <a href="${item.url}">${item.product_title}</a>
        </h2>

        <div class="CartItem__Meta Heading Text--subdued"><p class="CartItem__Variant">${variantTitle}</p><div class="CartItem__PriceList"><span class="CartItem__Price Price" data-money-convertible="">$ ${item.final_line_price / 100
        }</span></div>
        </div><div class="CartItem__Actions Heading Text--subdued" style="text-align: center">
            <div class="CartItem__QuantitySelector">

              <div class="QuantitySelector">
              <a class="QuantitySelector__Button Link Link--primary" title="Set quantity to ${item.quantity - 1
        }" href="/cart/change?quantity=${item.quantity - 1}&amp;id=${item.key}" data-quantity="${item.quantity - 1
        }" data-line-id="${item.key
        }" data-action="update-item-quantity"><svg class="Icon Icon--minus" role="presentation" viewBox="0 0 16 2"<path d="M1,1 L15,1" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"></path></svg></a>

                <input type="text" name="updates[]" id="updates_${item.key
        }" class="QuantitySelector__CurrentQuantity" pattern="[0-9]*" data-line-id="${item.key}" value="${item.quantity
        }">

                <a class="QuantitySelector__Button Link Link--primary" title="Set quantity to ${item.quantity + 1
        }" href="/cart/change?quantity=${item.quantity + 1}&amp;id=${item.key}" data-quantity="${item.quantity + 1
        }" data-line-id="${item.key
        }" data-action="update-item-quantity"><svg class="Icon Icon--plus" role="presentation" viewBox="0 0 16 16"><g stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"><path d="M8,1 L8,15"></path><path d="M1,8 L15,8"></path></g></svg></a>
              </div>
            </div>

            <a href="/cart/change?quantity=0&amp;id=${item.key
        }" class="CartItem__Remove Link Link--underline Link--underlineShort" data-quantity="0" data-line-id="${item.key
        }" data-action="remove-item">Remove</a>
          </div>
      </div></div></div>`

      cart.elements.itemList.insertAdjacentHTML('beforeend', html)
    }

    if (typeof callback === 'function') {
      callback()
    }
  },
  updateCartAttributes(data, callback) {
    const params = {
      type: 'POST',
      url: '/cart/update.js',
      data,
      dataType: 'json',
      success(cart_data) {
        if (typeof callback === 'function') {
          document.dispatchEvent(new CustomEvent('theme:loading:end'))
          $('.Header__CartCount').html(cart_data.item_count)
          callback(cart_data)
          cart.rebuildCart(cart_data, cart.openCartDrawer)
        } else {
          Shopify.api.onCartUpdate(cart_data)
        }
      },
      error(XMLHttpRequest, textStatus) {
        Shopify.api.onError(XMLHttpRequest, textStatus)
      }
    }
    $.ajax(params)
  }
}
export { cart }

export function modalSwatches(swatches, forcePerRowMobile) {
  if (window.viewportWidth < window.mainBreakpoint) {
    // console.log("mobile");
    if (swatches.length > 0) {
      const style = getComputedStyle(swatches[0])
      const margin = parseInt(style.marginRight)
      // console.log(margin);
      const width = `calc(100% / ${forcePerRowMobile} - ${margin}px)`
      // console.log(`width is ${width}`);

      for (const swatch of swatches) {
        // console.log(width);
        swatch.style.width = width
        const heightMatchWidth = swatch.getBoundingClientRect().width
        // console.log(`height is ${heightMatchWidth}`);
        swatch.style.height = `${heightMatchWidth}px`
      }
    }
  } else {
    for (const swatch of swatches) {
      swatch.style.width = ''
      swatch.style.height = ''
    }
  }
}

export function matchSwatchesWidth(colorsWrapper, gridItems, elements, forcePerRow) {
  if (gridItems[0] && typeof forcePerRow === 'number' && forcePerRow > 0) {
    const wrapperWidth = colorsWrapper.getBoundingClientRect().width
    const style = getComputedStyle(gridItems[0])
    const margin = parseInt(style.marginLeft) + parseInt(style.marginRight)
    const itemWidth = `${wrapperWidth / forcePerRow - margin}px`

    const borderWidth = style.borderBottomWidth

    for (const item of gridItems) {
      item.style.width = itemWidth
      item.style.height = itemWidth

      const mask = item.querySelector('.mask')
      const hover = item.querySelector('.hover')
      if (mask) {
        mask.style.width = itemWidth
        mask.style.height = itemWidth
      }

      if (hover) {
        hover.style.width = itemWidth - borderWidth * 2
        hover.style.height = itemWidth - borderWidth * 2
      }
    }

    for (const el of elements) {
      if (el !== null) {
        el.style.width = `${wrapperWidth - parseInt(style.marginRight)}px`
      }
    }
  } else {
    const numPerRow = getNumPerRow(gridItems)
    console.log(`num per row = ${numPerRow}`)
    if (gridItems[0]) {
      const style = getComputedStyle(gridItems[0])
      const borderWidth = style.borderBottomWidth

      // Resets the width/height if set inline
      if (typeof forcePerRow !== 'number') {
        for (const item of gridItems) {
          item.style.width = ''
          item.style.height = ''

          const mask = item.querySelector('.mask')
          const hover = item.querySelector('.hover')
          if (mask) {
            mask.style.width = ''
            mask.style.height = ''
          }

          if (hover) {
            hover.style.width = ''
            hover.style.height = ''
          }
        }
      }

      // Get width of one swatch, since they are all the same width
      const baseElWidth = gridItems[0].getBoundingClientRect().width
      const finalElWidth = outerWidth(gridItems[0])
      const marginWidth = finalElWidth - baseElWidth

      const width = finalElWidth * (numPerRow - 1) + baseElWidth

      for (const el of elements) {
        if (el !== null) {
          el.style.width = `${width}px`
        }
      }
    }
  }
}

// Wrap an HTMLElement around another HTMLElement or an array of them
// NOTE: One wrapper is made, it wraps all of the elements
export function wrapAll(wrapperElement, elementsToWrap) {
  console.log(wrapperElement)
  console.log(elementsToWrap)

  for (let i = 0; i < elementsToWrap.length; i++) {
    wrapperElement.appendChild(elementsToWrap[i])
  }
}
